import React, { useState } from 'react';
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton, Divider, Fade, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../context/LanguageContext';
import { CDN_BASE_URL } from '../config/api';
import { buildExamOverviewPath, buildExamTipPath } from '../utils/contentRoutes';
import {
  ANCHOR_SCROLL_MARGIN,
  CONTENT_MAX_WIDTH,
  TEXT_MAX_WIDTH,
  accentRuleSx,
  ctaSecondarySx,
  ctaTertiarySx,
} from '../styles/tokens';
import { BORDER, INK, MUTED, SURFACE } from '../pages/Practice/map/mapTokens';
import { SECTION_IDS } from './home/homeContent';
import { ChapterChip, IconTile, bandSx } from './home/homeUi';

const oralIcon = `${CDN_BASE_URL}/stick-oral.png?width=80&height=83`;
const writtenIcon = `${CDN_BASE_URL}/stick-written.png?width=80&height=80`;
/** A floor, not a fixed height — the two cards stretch to match via the grid. */
const CARD_MIN_HEIGHT = { xs: 'auto', sm: 420 };

/*
 * Task icons replaced the emoji these lists used to carry. Emoji render as a
 * different glyph on every platform, are fully saturated against a restrained
 * navy palette, and are announced by screen readers as "eyes" / "telephone
 * receiver" / "spiral calendar" — noise next to the label that already says it.
 */
const oralA1A2 = [
  {
    icon: 'Image',
    title: 'Describe a picture',
    text: 'Talk about what you see in an image.',
    hash: '#oral-spoken-task-describe-image',
  },
  {
    icon: 'Phone',
    title: 'Make a pretend call',
    text: 'Simulate a short phone conversation.',
    hash: '#oral-spoken-task-telephone',
  },
  {
    icon: 'CalendarMonth',
    title: 'Share your routine',
    text: 'Describe your daily or weekly activities.',
    hash: '#oral-spoken-task-discussion',
  },
];
const oralA2B1 = [
  {
    icon: 'RecordVoiceOver',
    title: 'Share opinions or plans',
    text: 'Express ideas or future intentions.',
    hash: '#oral-spoken-level-a2-b1',
  },
  {
    icon: 'Category',
    title: 'Choose topics',
    text: 'Pick from several subjects to discussion.',
    hash: '#oral-spoken-level-a2-b1',
  },
  {
    icon: 'QuestionAnswer',
    title: 'Converse with depth',
    text: 'Engage in more detailed conversations.',
    hash: '#oral-spoken-level-a2-b1',
  },
];
const writtenA1A2 = [
  {
    icon: 'EditNote',
    title: 'Fill simple forms',
    text: 'Complete very basic forms.',
    hash: '#written-track-a1-a2-module-1',
  },
  {
    icon: 'Sms',
    title: 'Respond to short messages',
    text: 'Write quick replies to short messages.',
    hash: '#written-track-a1-a2-module-3',
  },
  {
    icon: 'MarkEmailUnread',
    title: 'Short replies to friends or offices',
    text: 'Answer simple letters or notes.',
    hash: '#written-track-a1-a2-module-3',
  },
];
const writtenA2B1 = [
  {
    icon: 'Description',
    title: 'Fill detailed forms',
    text: 'Handle more complex paperwork.',
    hash: '#written-track-a2-b1',
  },
  {
    icon: 'MenuBook',
    title: 'Read real-world texts',
    text: 'Understand everyday written materials.',
    hash: '#written-track-a2-b1-module-5',
  },
  {
    icon: 'Drafts',
    title: 'Write letters, explanations',
    text: 'Write longer, explanatory texts.',
    hash: '#written-track-a2-b1-module-6',
  },
];

/*
 * Both level lists stay mounted and share one grid cell, so on mobile the card
 * is always as tall as the longer list and does not jump when the toggle flips.
 * The inactive list is hidden (not just transparent) so its links stay out of
 * the tab order and the a11y tree. On sm+ the cards already equalise against
 * their sibling via the grid, so the inactive list is dropped from layout.
 */
function StepList({ steps, overviewPath, active }) {
  return (
    <Box
      aria-hidden={!active}
      sx={{
        gridArea: '1 / 1',
        display: { xs: 'block', sm: active ? 'block' : 'none' },
        visibility: active ? 'visible' : 'hidden',
        opacity: active ? 1 : 0,
        transition: 'opacity 300ms',
      }}
    >
      {steps.map((step, idx) => (
        <Fade in key={step.title} timeout={600 + idx * 250}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              mb: idx === steps.length - 1 ? 0 : 2,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0, flex: 1 }}>
              <IconTile name={step.icon} size={38} iconSize={20} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="primary.main"
                  component={RouterLink}
                  to={`${overviewPath}${step.hash}`}
                  sx={{ textDecoration: 'none', color: 'primary.main' }}
                >
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.text}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      ))}
    </Box>
  );
}

function CardJourney({ title, icon, levels, titleTo, moreInfoTo, levelLinks, overviewPath }) {
  const [level, setLevel] = useState('A1A2');
  const currentTitleTo = levelLinks?.[level]?.titleTo || titleTo;
  const currentMoreInfoTo = levelLinks?.[level]?.moreInfoTo || moreInfoTo;
  return (
    <Paper
      elevation={6}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: '16px',
        // White, not the theme's pastel-blue `background.paper` — a pastel card on
        // a whitesmoke band washes out and takes the body copy with it.
        bgcolor: SURFACE,
        boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 10px 24px -14px rgba(16,24,40,.14)',
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        minHeight: CARD_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        mx: 'auto',
        mb: { xs: 3, md: 0 },
        position: 'relative',
        overflow: 'visible',
        backdropFilter: 'blur(12px)',
        border: `1px solid ${BORDER}`,
      }}
    >
      <Box sx={{ position: 'relative', mb: 2, minHeight: 80 }}>
        <Box sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <Box component="img" src={icon} alt="card icon" loading="lazy" width="80" height="80" sx={{ width: 80 }} />
        </Box>
        <Typography
          variant="h5"
          fontWeight={900}
          color="primary.main"
          component={RouterLink}
          to={currentTitleTo}
          sx={{
            textAlign: 'center',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            px: 10,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {title}
        </Typography>
      </Box>
      <ToggleButtonGroup
        value={level}
        exclusive
        onChange={(_, val) => val && setLevel(val)}
        sx={{ mb: 2, width: '100%' }}
        fullWidth
        color="secondary"
      >
        <ToggleButton value="A1A2" translate="no" className="notranslate" sx={{ fontWeight: 700, flex: 1 }}>
          A1-A2
        </ToggleButton>
        <ToggleButton value="A2B1" translate="no" className="notranslate" sx={{ fontWeight: 700, flex: 1 }}>
          A2-B1
        </ToggleButton>
      </ToggleButtonGroup>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ flexGrow: 1, display: 'grid', alignContent: 'start' }}>
        <StepList steps={levels.A1A2} overviewPath={overviewPath} active={level === 'A1A2'} />
        <StepList steps={levels.A2B1} overviewPath={overviewPath} active={level === 'A2B1'} />
      </Box>
      <Box sx={{ pt: 2, mt: 'auto', display: 'flex', justifyContent: 'center' }}>
        <Button
          component={RouterLink}
          to={currentMoreInfoTo}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '999px',
            px: 2.5,
            minWidth: { xs: 0, md: 140 },
          }}
        >
          {title} exam details
        </Button>
      </Box>
    </Paper>
  );
}

const ExamSection = ({ tone }) => {
  const { language } = useLanguage();
  const examOverviewPath = buildExamOverviewPath(language);
  return (
    <Box
      component="section"
      id={SECTION_IDS.understand}
      sx={{ ...bandSx(tone), scrollMarginTop: ANCHOR_SCROLL_MARGIN, '&:focus': { outline: 'none' } }}
    >
      <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', mb: { xs: 4, md: 6 } }}>
        <ChapterChip chapter="understand" />
        <Typography variant="h3" component="h2" fontWeight={900} color="primary.main" sx={{ mb: 1.5 }}>
          What's in the fide test?
        </Typography>
        <Box aria-hidden sx={{ ...accentRuleSx, mb: 2 }} />
        <Typography
          sx={{
            maxWidth: TEXT_MAX_WIDTH,
            mb: 1.5,
            color: MUTED,
            fontSize: { xs: '1rem', md: '1.125rem' },
            lineHeight: 1.7,
          }}
        >
          Understand what to expect in the fide test through simple, real-life tasks that check your speaking and
          writing skills.
        </Typography>
        {/* Explains the A1-A2 / A2-B1 toggle below, which otherwise looks like a
            filter rather than the two tracks the exam actually branches into. */}
        <Typography
          sx={{
            maxWidth: 720,
            color: INK,
            fontSize: { xs: '0.9375rem', md: '1rem' },
            lineHeight: 1.7,
            fontWeight: 600,
          }}
        >
          The oral and written parts are graded separately — you can choose the level you need.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          alignItems: 'stretch',
          gap: { xs: 2, md: 4 },
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', width: '100%' }}>
          <CardJourney
            title="Oral"
            icon={oralIcon}
            levels={{ A1A2: oralA1A2, A2B1: oralA2B1 }}
            titleTo={`${examOverviewPath}#oral`}
            moreInfoTo={`${examOverviewPath}#oral`}
            levelLinks={{
              A2B1: {
                titleTo: `${examOverviewPath}#oral-spoken-level-a2-b1`,
                moreInfoTo: `${examOverviewPath}#oral-spoken-level-a2-b1`,
              },
            }}
            overviewPath={examOverviewPath}
          />
        </Box>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <CardJourney
            title="Written"
            icon={writtenIcon}
            levels={{ A1A2: writtenA1A2, A2B1: writtenA2B1 }}
            titleTo={`${examOverviewPath}#written`}
            moreInfoTo={`${examOverviewPath}#written`}
            overviewPath={examOverviewPath}
          />
        </Box>
      </Box>
      {/* One secondary CTA and one text link, where there used to be three
          identical gradient pills. The mock-exam button moved to its own
          section, which is the only reason it is visible at all now. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          mt: { xs: 4, md: 5 },
        }}
      >
        <Button component={RouterLink} to={examOverviewPath} variant="outlined" size="large" sx={ctaSecondarySx}>
          Full exam structure
        </Button>
        <Button
          component={RouterLink}
          to={buildExamTipPath(language || 'fr')}
          endIcon={<ArrowForward />}
          sx={ctaTertiarySx}
        >
          Exam tips &amp; strategies
        </Button>
      </Box>
    </Box>
  );
};

export default ExamSection;
